import { useEffect, useMemo, useState } from 'react'
import { Activity } from 'lucide-react'
import { useLiveStore } from '../../../stores/live'

const WINDOW_S = 300
const BUCKET_S = 10
const BUCKETS = WINDOW_S / BUCKET_S

/**
 * 触发能量谱（地图 overlay）：近 5 分钟触发按 10s 桶聚合，
 * CAV 对数求和归一为柱高。confirmed 桶标红。
 * Android 端不上报原始波形，本条以真实触发特征值聚合呈现，
 * 不伪造波形——真波形通道待端上链路落地后替换。
 */
export function TriggerEnergyStrip() {
  const triggers = useLiveStore((s) => s.triggers)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const perMin = useMemo(
    () => triggers.filter((t) => now - t.ts <= 60_000).length,
    [triggers, now],
  )

  const bars = useMemo(() => {
    const cutoff = now - WINDOW_S * 1000
    const sums = new Array<number>(BUCKETS).fill(0)
    const confirmed = new Array<boolean>(BUCKETS).fill(false)
    for (const t of triggers) {
      if (t.ts < cutoff) continue
      const idx = BUCKETS - 1 - Math.floor((now - t.ts) / (BUCKET_S * 1000))
      if (idx < 0 || idx >= BUCKETS) continue
      sums[idx] += Math.log1p(Math.max(0, t.cav))
      if (t.confirmed) confirmed[idx] = true
    }
    const max = Math.max(1, ...sums)
    return sums.map((sum, i) => ({
      height: sum > 0 ? Math.max(8, (sum / max) * 100) : 0,
      confirmed: confirmed[i],
      fresh: i === BUCKETS - 1 && sum > 0,
    }))
  }, [triggers, now])

  return (
    <div className="panel-glass absolute bottom-3 right-3 z-10 w-[340px] rounded-md">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.2em] text-muted-foreground">
          <Activity size={12} className="text-primary" /> 触发能量 · 近 5 分钟
        </div>
        <span className="font-mono text-[10px] tabular-nums text-muted-foreground">{perMin} 次/分</span>
      </div>
      <div className="px-3 py-2">
        <svg viewBox="0 0 320 44" width="100%" role="img" aria-label="近 5 分钟触发能量谱">
          <title>触发能量谱</title>
          <desc>近 5 分钟终端触发能量的时间分布，红色柱表示包含已确认触发</desc>
          <line x1="0" y1="43" x2="320" y2="43" stroke="rgba(148,163,196,0.25)" strokeWidth="0.5" />
          {bars.map((b, i) => {
            const x = i * (320 / BUCKETS)
            const w = 320 / BUCKETS - 2
            if (b.height === 0) {
              return <rect key={i} x={x} y={41.5} width={w} height={1.5} fill="rgba(139,152,171,0.35)" />
            }
            const h = (b.height / 100) * 40
            const fill = b.confirmed ? '#ff4d5e' : '#56d7ff'
            return (
              <rect
                key={i}
                x={x}
                y={42 - h}
                width={w}
                height={h}
                fill={fill}
                opacity={b.fresh ? 1 : 0.55}
              />
            )
          })}
        </svg>
        <div className="mt-1 flex justify-between font-mono text-[9px] text-muted-foreground/60">
          <span>-5min</span>
          <span className="text-muted-foreground/40">10s/格 · 红=已确认</span>
          <span>现在</span>
        </div>
      </div>
    </div>
  )
}
